/**
 * useChatAttachments — single hook to manage all attachment state for one chat thread.
 *
 * Responsibilities:
 *   - Maintain the local "drafts" array (files chosen via picker, mid-upload)
 *   - Drive uploads to the server and reconcile responses
 *   - Poll the server for status transitions (pending → processing → ready/failed)
 *   - Expose helpers (remove, confirmParse) the chip needs
 *
 * The hook is one source of truth for the chat composer.  ChatPanel renders
 * the picker + chip strip from this hook and submits `usableAttachmentIds`
 * with the next user turn.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { trackEvent } from '../../utils/analytics';
import { useCopilotStore } from '../../pages/copilot/stores/useCopilotStore';
import { makeDraftFromFile } from './AttachmentPicker';
import {
  confirmParse,
  deleteAttachment,
  listAttachments,
  uploadAttachments,
} from './api';
import type { DraftAttachment, ServerAttachment } from './types';

const POLL_INITIAL_MS = 1_500;
const POLL_MAX_MS = 8_000;

/**
 * Stable id for a draft.  We always overwrite this in the post-upload
 * step with the server's attachment_id so a single chip survives the
 * draft → uploaded transition without React thinking it's a different
 * row.
 */
function isUploading(d: DraftAttachment): boolean {
  return d.uploadState === 'queued' || d.uploadState === 'uploading';
}

function isProcessing(a: ServerAttachment): boolean {
  return a.status === 'pending' || a.status === 'processing';
}

export interface UseChatAttachmentsOptions {
  tenantId: string;
  threadId: string | null | undefined;
  /** When true, hydrates server-side state on mount and thread change. */
  hydrate?: boolean;
}

export interface UseChatAttachmentsApi {
  /** Pending uploads (still local) — used to render in-flight chips. */
  drafts: DraftAttachment[];
  /** Server-side rows — used to render ready/processing/failed chips. */
  serverAttachments: ServerAttachment[];
  /** True if any draft is still uploading or any server row is still processing. */
  isBusy: boolean;
  /** True if any server row has parse_confidence=low and hasn't been confirmed. */
  hasUnconfirmedLowConfidence: boolean;
  /** IDs the agent can actually use right now (ready + confirmed-if-needed). */
  usableAttachmentIds: string[];

  addFiles: (files: File[]) => Promise<void>;
  removeDraft: (id: string) => void;
  remove: (attachmentId: string) => Promise<void>;
  confirm: (attachmentId: string) => Promise<void>;
  clearAll: () => void;
  /** Force re-fetch of server state (e.g. after sending a message). */
  refresh: () => Promise<void>;
}

export function useChatAttachments(opts: UseChatAttachmentsOptions): UseChatAttachmentsApi {
  const { tenantId, threadId, hydrate = true } = opts;

  const [drafts, setDrafts] = useState<DraftAttachment[]>([]);
  const [serverAttachments, setServerAttachments] = useState<ServerAttachment[]>([]);
  const pollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Reset when the active thread changes.
  useEffect(() => {
    setDrafts([]);
    setServerAttachments([]);
  }, [threadId]);

  const upsertCache = useCopilotStore((s) => s.upsertAttachmentCache);
  const clearCache = useCopilotStore((s) => s.clearAttachmentCache);

  // Clear shared cache when the thread changes so a previous thread's
  // attachments don't leak into chips on the new thread.
  useEffect(() => {
    clearCache();
  }, [threadId, clearCache]);

  const refresh = useCallback(async () => {
    if (!tenantId || !threadId) return;
    try {
      // Always pull deleted rows too so the shared attachment cache stays
      // populated for historical chips after the user removes a file from
      // the composer.  The composer state below filters them back out.
      const rows = await listAttachments(tenantId, threadId, {
        includeDeleted: true,
      });
      setServerAttachments(rows.filter((r) => !r.deleted_at));
      upsertCache(rows);
    } catch (err) {
      console.warn('useChatAttachments.refresh failed', err);
    }
  }, [tenantId, threadId, upsertCache]);

  // Hydrate once on mount + when thread changes.
  useEffect(() => {
    if (hydrate && tenantId && threadId) {
      void refresh();
    }
  }, [hydrate, tenantId, threadId, refresh]);

  // Status poll while anything is in flight (pending/processing).  Exponential
  // backoff so a stuck processor doesn't hammer the API.
  useEffect(() => {
    if (!tenantId || !threadId) return;
    const anyProcessing = serverAttachments.some(isProcessing);
    if (!anyProcessing) return;

    let interval = POLL_INITIAL_MS;
    const tick = async () => {
      await refresh();
      interval = Math.min(interval * 1.5, POLL_MAX_MS);
      pollTimer.current = setTimeout(tick, interval);
    };
    pollTimer.current = setTimeout(tick, interval);

    return () => {
      if (pollTimer.current) {
        clearTimeout(pollTimer.current);
        pollTimer.current = null;
      }
    };
  }, [serverAttachments, tenantId, threadId, refresh]);

  const addFiles = useCallback(
    async (files: File[]) => {
      if (!tenantId || !threadId || files.length === 0) return;

      // Optimistic drafts so chips appear immediately.
      const newDrafts: DraftAttachment[] = files.map((f, i) =>
        makeDraftFromFile(f, Date.now() + i, false),
      );
      setDrafts((prev) => [...prev, ...newDrafts]);

      try {
        // Mark uploading
        setDrafts((prev) =>
          prev.map((d) =>
            newDrafts.find((nd) => nd.id === d.id)
              ? { ...d, uploadState: 'uploading' }
              : d,
          ),
        );

        const uploaded = await uploadAttachments(tenantId, threadId, files);

        // Replace optimistic drafts with their server siblings.  The chip
        // component keys off serverAttachment.attachment_id when present,
        // so we mark the drafts complete and let serverAttachments take
        // over from here.
        setDrafts((prev) =>
          prev.map((d, idx) => {
            const i = newDrafts.findIndex((nd) => nd.id === d.id);
            if (i < 0) return d;
            const match = uploaded[i];
            return match
              ? { ...d, uploadState: 'uploaded', serverAttachment: match }
              : d;
          }),
        );

        setServerAttachments((prev) => [...prev, ...uploaded]);
        upsertCache(uploaded);

        uploaded.forEach((a) =>
          trackEvent('copilot_attachment_uploaded', {
            attachment_id: a.attachment_id,
            tenant_id: tenantId,
            thread_id: threadId,
            kind: a.kind,
            size_bytes: a.size_bytes,
          }),
        );
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : String(err);
        setDrafts((prev) =>
          prev.map((d) =>
            newDrafts.find((nd) => nd.id === d.id)
              ? { ...d, uploadState: 'error', uploadError: msg }
              : d,
          ),
        );
      }
    },
    [tenantId, threadId],
  );

  const removeDraft = useCallback((id: string) => {
    setDrafts((prev) => prev.filter((d) => d.id !== id));
  }, []);

  const remove = useCallback(
    async (attachmentId: string) => {
      if (!tenantId || !threadId) return;
      try {
        await deleteAttachment(tenantId, threadId, attachmentId);
      } catch (err) {
        console.warn('useChatAttachments.remove failed', err);
      }
      // Pull the file out of the composer.
      setServerAttachments((prev) => {
        const removed = prev.find((a) => a.attachment_id === attachmentId);
        // Keep the metadata in the shared cache so any historical user
        // message that referenced this file still renders with the proper
        // filename + preview — just flagged as removed.
        if (removed) {
          upsertCache([{ ...removed, deleted_at: new Date().toISOString() }]);
        }
        return prev.filter((a) => a.attachment_id !== attachmentId);
      });
      // Also yank any drafts whose server sibling matches (so a chip
      // that's "uploaded" gets removed too).
      setDrafts((prev) =>
        prev.filter((d) => d.serverAttachment?.attachment_id !== attachmentId),
      );
    },
    [tenantId, threadId, upsertCache],
  );

  const confirm = useCallback(
    async (attachmentId: string) => {
      if (!tenantId || !threadId) return;
      try {
        const updated = await confirmParse(tenantId, threadId, attachmentId);
        setServerAttachments((prev) =>
          prev.map((a) =>
            a.attachment_id === attachmentId ? updated : a,
          ),
        );
        upsertCache([updated]);
        trackEvent('copilot_attachment_parse_low_confidence', {
          attachment_id: attachmentId,
          tenant_id: tenantId,
          thread_id: threadId,
          confirmed: true,
        });
      } catch (err) {
        console.warn('useChatAttachments.confirm failed', err);
      }
    },
    [tenantId, threadId],
  );

  const clearAll = useCallback(() => {
    setDrafts([]);
  }, []);

  const isBusy = useMemo(
    () =>
      drafts.some(isUploading) ||
      serverAttachments.some(isProcessing),
    [drafts, serverAttachments],
  );

  const hasUnconfirmedLowConfidence = useMemo(
    () =>
      serverAttachments.some(
        (a) =>
          a.status === 'ready' &&
          a.parse_confidence === 'low' &&
          !a.parse_confirmed_at,
      ),
    [serverAttachments],
  );

  const usableAttachmentIds = useMemo(
    () =>
      serverAttachments
        .filter(
          (a) =>
            a.status === 'ready' &&
            (a.parse_confidence !== 'low' || !!a.parse_confirmed_at),
        )
        .map((a) => a.attachment_id),
    [serverAttachments],
  );

  return {
    drafts,
    serverAttachments,
    isBusy,
    hasUnconfirmedLowConfidence,
    usableAttachmentIds,
    addFiles,
    removeDraft,
    remove,
    confirm,
    clearAll,
    refresh,
  };
}
